/**
 * Disregard dialog of the F1 inbox (PROD-3274 DF-04). A reason is required; the note is optional and holds at most 500
 * characters. The file leaves the inbox but stays in the audit record: nothing is deleted (contract §3, soft delete).
 */
import { useState } from 'react';
import { ModalYesNo, TextArea } from '@scripsteam/scrips-signal-ds';
import { ChoiceList } from '@/shared/ui/components/choice-list';
import type { InboxDisregardReason } from '../types';

export const DISREGARD_NOTE_MAX = 500;

const REASONS: { value: InboxDisregardReason; label: string }[] = [
  { value: 'not_this_patient', label: 'Not this patient' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'not_clinical', label: 'Not clinical' },
  { value: 'other', label: 'Other' },
];

export interface InboxDisregardDialogProps {
  fileName: string | null;
  pending: boolean;
  /** Why the last Disregard failed (`inboxActionError`); null when it did not. */
  failure: string | null;
  onConfirm: (reason: InboxDisregardReason, note: string | null) => void;
  onCancel: () => void;
}

export function InboxDisregardDialog({ fileName, pending, failure, onConfirm, onCancel }: InboxDisregardDialogProps) {
  const [reason, setReason] = useState<InboxDisregardReason | null>(null);
  const [note, setNote] = useState('');
  return (
    <ModalYesNo
      open={fileName !== null}
      title={`Disregard ${fileName ?? ''}?`}
      confirmLabel="Disregard"
      cancelLabel="Cancel"
      destructive
      loading={pending}
      confirmDisabled={reason === null}
      onCancel={onCancel}
      onConfirm={() => reason && onConfirm(reason, note.trim() === '' ? null : note.trim())}
      message={
        <div data-slot="intake-disregard-body">
          <p data-slot="intake-disregard-lead">
            Removes the file from the inbox. It is kept in the audit record, never deleted.
          </p>
          <fieldset data-slot="intake-disregard-reasons">
            <legend>Reason</legend>
            {/* The reasons as the design PaChoiceList cards (shared adapter); the same one-of-four choice. */}
            <ChoiceList
              label="Reason"
              items={REASONS.map((r) => ({ id: r.value, primary: r.label }))}
              selectedId={reason}
              onSelect={(id) => setReason(id as InboxDisregardReason)}
            />
          </fieldset>
          <TextArea
            label="Note (optional)"
            value={note}
            maxLength={DISREGARD_NOTE_MAX}
            rows={3}
            helperText={`${note.length} of ${DISREGARD_NOTE_MAX} characters`}
            onChange={(e) => setNote(e.target.value.slice(0, DISREGARD_NOTE_MAX))}
          />
          {failure ? (
            <p role="alert" data-slot="intake-disregard-error">
              {failure}
            </p>
          ) : null}
        </div>
      }
    />
  );
}
